import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Edit, Trash2, Download, Upload, Copy, AlertCircle } from 'lucide-react';
import integrationsService from '../../services/integrationsService';
import { apiErrorMessage } from '../../utils/apiError';
import Button from '../shared/Button';
import Spinner from '../shared/Spinner';
import ConfirmModal from '../shared/ConfirmModal';
import { SubTabs } from '../shared/Tabs';
import { ConversionEditModal, ConversionCSVUploadModal } from '../shared/ConversionModals';
import '../TaskConfigurationPage.css';   // .conversions-table / .conversion-filters / .table-actions — the sync screen's own

// THE CONVERSIONS TAB OF A PIPELINE IMPLEMENTATION (V721) — the sync task's Conversions screen, for a pipeline.
//
// One sub-tab per ADAPTER whose system has pipeline conversions (marketplace.has_pipeline_conversions — the OHIP
// Adapter). Each lists the implementation's rows for that adapter — record type, field, source value → target value —
// with the same filters, Add / Edit / Delete, CSV example, download and upload, and Copy from another implementation
// of the template, as the sync screen offers. The record types and fields come from the server
// (getConversionTypes): exactly what the adapter's code field map converts, described by the sync vocabulary, so a
// row the screen accepts is one the adapter applies. Writes are superadmin only; the server enforces it too.
//
//   templateId, implementationId   the implementation
//   nodes                          the template's graph nodes (the adapters are the SOURCE ones with the flag)
//   siblings                       this template's other implementations, for Copy from

const csvLine = (values) => values.map(v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`).join(',');
// A small CSV reader: quoted fields, doubled quotes, CRLF. The columns are named by the header row.
const parseCsv = (text) => {
    const rows = [];
    let row = [], field = '', quoted = false;
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (quoted) {
            if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
            else if (c === '"') quoted = false;
            else field += c;
        } else if (c === '"') quoted = true;
        else if (c === ',') { row.push(field); field = ''; }
        else if (c === '\n' || c === '\r') { if (c === '\r' && text[i + 1] === '\n') i++; row.push(field); rows.push(row); row = []; field = ''; }
        else field += c;
    }
    if (field !== '' || row.length) { row.push(field); rows.push(row); }
    const header = (rows.shift() || []).map(h => h.trim());
    return rows.filter(r => r.some(v => v.trim() !== '')).map(r => Object.fromEntries(header.map((h, i) => [h, (r[i] || '').trim()])));
};

const download = (name, content, type) => {
    const url = window.URL.createObjectURL(content instanceof Blob ? content : new Blob([content], { type }));
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click();
    window.URL.revokeObjectURL(url); document.body.removeChild(a);
};

export default function PipelineConversionsTab({ templateId, implementationId, nodes = [], siblings = [], isSuperAdmin = false }) {
    const adapters = useMemo(() => (nodes || []).filter(n => n.role === 'SOURCE' && n.has_pipeline_conversions === 'Y'), [nodes]);
    const [nodeId, setNodeId] = useState(adapters.length ? String(adapters[0].id) : '');
    useEffect(() => { if (adapters.length && !adapters.some(a => String(a.id) === nodeId)) setNodeId(String(adapters[0].id)); }, [adapters, nodeId]);
    const adapter = adapters.find(a => String(a.id) === nodeId) || null;

    const [types, setTypes] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [filterType, setFilterType] = useState('');
    const [filterField, setFilterField] = useState('');
    const [filterSearch, setFilterSearch] = useState('');
    const [editing, setEditing] = useState(null);      // null closed, {} add, row edit
    const [uploadOpen, setUploadOpen] = useState(false);
    const [copyOpen, setCopyOpen] = useState(false);
    const [copyFrom, setCopyFrom] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = useCallback(async () => {
        if (!nodeId) return;
        setLoading(true); setError(null);
        try {
            const [t, r] = await Promise.all([
                integrationsService.getConversionTypes(templateId, implementationId, nodeId),
                integrationsService.listConversions(templateId, implementationId, nodeId),
            ]);
            setTypes(t.types || []);
            setRows(r.conversions || []);
        } catch (e) {
            setError(apiErrorMessage(e, 'The conversions could not be loaded.'));
        } finally { setLoading(false); }
    }, [templateId, implementationId, nodeId]);
    useEffect(() => { load(); }, [load]);

    const describe = (scope, field) => {
        const t = types.find(x => x.scopeType === scope);
        const f = t && (t.fields || []).find(x => x.field === field);
        return { scope: (t && t.scopeDescription) || scope, field: (f && f.description) || field };
    };
    const filtered = rows.filter(r => (!filterType || r.recordType === filterType) && (!filterField || r.sourceFieldName === filterField)
        && (!filterSearch.trim() || [r.recordType, r.sourceFieldName, r.sourceValue, r.targetValue, describe(r.recordType, r.sourceFieldName).field]
            .some(v => String(v || '').toLowerCase().includes(filterSearch.trim().toLowerCase()))));
    const typesInUse = [...new Set(rows.map(r => r.recordType))].map(s => [s, describe(s).scope]);
    const fieldsInUse = filterType ? [...new Set(rows.filter(r => r.recordType === filterType).map(r => r.sourceFieldName))].map(f => [f, describe(filterType, f).field]) : [];

    const save = async (form) => {
        // The dialog shows its own error; a success refreshes the list behind it.
        if (editing && editing.conversionUuid) await integrationsService.updateConversion(templateId, implementationId, editing.conversionUuid, form);
        else await integrationsService.createConversion(templateId, implementationId, nodeId, form);
        await load();
    };
    const remove = async (row) => {
        setConfirmDelete(null);
        try { await integrationsService.deleteConversion(templateId, implementationId, row.conversionUuid); setNotice('Conversion deleted.'); await load(); }
        catch (e) { setError(apiErrorMessage(e, 'The conversion could not be deleted.')); }
    };
    const exampleCsv = () => {
        const lines = [csvLine(['recordType', 'sourceFieldName', 'sourceValue', 'targetValue', 'skip'])];
        for (const t of types) for (const f of t.fields || []) lines.push(csvLine([t.scopeType, f.field, 'A', 'B', 'N']));
        download(`conversions-example-${implementationId}.csv`, lines.join('\n'), 'text/csv');
    };
    const downloadCsv = async () => {
        try { download(`conversions-${implementationId}-${nodeId}.csv`, await integrationsService.downloadConversions(templateId, implementationId, nodeId), 'text/csv'); }
        catch (e) { setError(apiErrorMessage(e, 'The conversions could not be downloaded.')); }
    };
    const upload = async (file) => {
        const text = await file.text();
        const parsed = parseCsv(text);
        const res = await integrationsService.uploadConversions(templateId, implementationId, nodeId, parsed);
        setNotice(res.message || 'Conversions uploaded.');
        await load();
    };
    const copy = async () => {
        setCopyOpen(false);
        if (!copyFrom) return;
        try { const res = await integrationsService.copyConversions(templateId, implementationId, nodeId, Number(copyFrom)); setNotice(res.message || 'Copied.'); await load(); }
        catch (e) { setError(apiErrorMessage(e, 'The conversions could not be copied.')); }
    };

    if (!adapters.length) {
        return <p style={{ color: '#6b7280' }}>None of this pipeline's adapters has conversions.</p>;
    }
    const others = (siblings || []).filter(s => String(s.id) !== String(implementationId));

    return (
        <div>
            {adapters.length > 1 && (
                <SubTabs tabs={adapters.map(a => ({ key: String(a.id), label: a.adapter || a.system_name || `Adapter ${a.id}` }))} active={nodeId} onChange={setNodeId} />
            )}
            <div className="card-header" style={{ marginTop: adapters.length > 1 ? '1rem' : 0 }}>
                <h2 style={{ margin: 0 }}>Conversions{adapter ? ` — ${adapter.adapter || adapter.system_name}` : ''}</h2>
                <div className="header-actions">
                    <Button variant="secondary" onClick={exampleCsv} disabled={!types.length}><Download className="icon-sm" /> Download CSV Example</Button>
                    {isSuperAdmin && others.length > 0 && (
                        <Button variant="secondary" onClick={() => { setCopyFrom(''); setCopyOpen(true); }} className="sa-only"><Copy className="icon-sm" /> Copy from implementation</Button>
                    )}
                    <Button variant="secondary" onClick={downloadCsv} disabled={!rows.length}><Download className="icon-sm" /> Download CSV</Button>
                    {isSuperAdmin && <Button variant="secondary" onClick={() => setUploadOpen(true)} className="sa-only"><Upload className="icon-sm" /> Upload CSV</Button>}
                    {isSuperAdmin && <Button onClick={() => setEditing({})} className="sa-only"><Plus className="icon-sm" /> Add Conversion</Button>}
                </div>
            </div>
            <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '0.25rem 0 1rem' }}>
                Code values on every record this adapter sends on are converted for this property before any route sees them.
                A row with an empty source value is a default, filling a field the record left out; a skip removes the field.
            </p>

            {error && <div className="os-msg os-msg-error" style={{ marginBottom: '1rem' }}>{error}</div>}
            {notice && <div className="os-msg os-msg-success" style={{ marginBottom: '1rem' }}>{notice}</div>}

            <div className="conversion-filters">
                <div className="filter-group">
                    <label>Record Type</label>
                    <select value={filterType} onChange={(e) => { setFilterType(e.target.value); setFilterField(''); }} className="form-input">
                        <option value="">All Types</option>
                        {typesInUse.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                    </select>
                </div>
                {filterType && (
                    <div className="filter-group">
                        <label>Field</label>
                        <select value={filterField} onChange={(e) => setFilterField(e.target.value)} className="form-input">
                            <option value="">All Fields</option>
                            {fieldsInUse.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                        </select>
                    </div>
                )}
                <div className="filter-group filter-search">
                    <label>Search</label>
                    <input type="text" value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} placeholder="Search conversions..." className="form-input" />
                </div>
            </div>

            {loading ? (
                <div className="loading-section"><Spinner size="md" /><p>Loading conversions...</p></div>
            ) : filtered.length === 0 ? (
                <div className="empty-state">
                    <AlertCircle className="empty-icon" />
                    <p>{rows.length ? 'No conversions match these filters' : 'No conversions yet for this adapter'}</p>
                    {isSuperAdmin && !rows.length && <Button onClick={() => setEditing({})} className="sa-only"><Plus className="icon-sm" /> Add First Conversion</Button>}
                </div>
            ) : (
                <div className="conversions-table-wrapper">
                    <table className="conversions-table">
                        <thead>
                            <tr><th>Record Type</th><th>Field</th><th>Source Value</th><th>Target Value</th>{isSuperAdmin && <th>Actions</th>}</tr>
                        </thead>
                        <tbody>
                            {filtered.map(r => {
                                const d = describe(r.recordType, r.sourceFieldName);
                                return (
                                    <tr key={r.conversionUuid}>
                                        <td>{d.scope}</td>
                                        <td>{d.field}</td>
                                        <td>{r.sourceValue || <em style={{ color: '#6b7280' }}>(default)</em>}</td>
                                        <td>{r.skip ? <em style={{ color: '#92400e' }}>skipped — field removed</em> : r.targetValue}</td>
                                        {isSuperAdmin && (
                                            <td>
                                                <div className="table-actions">
                                                    <button className="action-btn" onClick={() => setEditing(r)} title="Edit"><Edit className="icon-sm" /></button>
                                                    <button className="action-btn delete" onClick={() => setConfirmDelete(r)} title="Delete"><Trash2 className="icon-sm" /></button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && (
                <ConversionEditModal
                    isOpen
                    onClose={() => setEditing(null)}
                    onSave={save}
                    conversion={editing.conversionUuid ? editing : null}
                    conversionDataTypes={types}
                    isDefault={false}
                />
            )}
            {uploadOpen && (
                <ConversionCSVUploadModal isOpen onClose={() => setUploadOpen(false)} onUpload={upload} isDefault={false} />
            )}
            {confirmDelete && (
                <ConfirmModal
                    title="Delete Conversion"
                    message={`Delete the ${describe(confirmDelete.recordType, confirmDelete.sourceFieldName).field} conversion "${confirmDelete.sourceValue || '(default)'}"? Records will be sent with the source value from now on.`}
                    confirmLabel="Delete"
                    danger
                    onConfirm={() => remove(confirmDelete)}
                    onCancel={() => setConfirmDelete(null)}
                />
            )}
            {copyOpen && (
                <ConfirmModal
                    title="Copy conversions from another implementation"
                    confirmLabel="Copy"
                    confirmDisabled={!copyFrom}
                    onConfirm={copy}
                    onCancel={() => setCopyOpen(false)}
                >
                    <p className="cm-message">Adds the other property's conversions for this adapter that this property does not have yet. Nothing here is changed or removed.</p>
                    <select className="form-input" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
                        <option value="">Choose an implementation…</option>
                        {others.map(s => <option key={s.id} value={s.id}>{s.hotelname || s.name || `Implementation ${s.id}`}</option>)}
                    </select>
                </ConfirmModal>
            )}
        </div>
    );
}
